import { useState, useEffect, useCallback } from 'react';
import { s, authHeaders, Toast, newRecordButtonStyle } from './listPageStyles';
import { ListPageShell } from './ui';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';
import './securityScreens.css';

// G-K-01 · KK-04 — Kullanıcı Grupları.
//
// Bu bir YETKİ ekranı DEĞİLDİR. Müşteri Grubu / Ürün Grubu ile aynı kalıpta düz bir sınıflandırma
// tanımıdır: rol taşımaz, yetki taşımaz, veri yetkisi taşımaz ve yetki çözümleme algoritmasına
// hiç girmez. Bu yüzden ekranda "Yetkiler" sekmesi YOK — olsaydı grubun yetki taşıdığı izlenimi
// verirdi ve KK-04 kararı ilk günden delinirdi.
//
// Kullanım amacı: raporlama kırılımı, toplu bilgilendirme hedefi ve KK-16 giriş penceresi kriteri.

const emptyForm = { code: '', name: '', description: '', isActive: true };

export function UserGroupsPage() {
  usePageMeta('Kullanıcı Grupları', 'Kullanıcı & Yetki');
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // { id } | 'new' | null
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/user-groups?$orderby=Code&$top=500', { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Gruplar okunamadı');
      setRows(body?.value || body?.data || []);
    } catch (e) {
      showToast('error', e.message || 'Gruplar okunamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing('new'); setForm(emptyForm); };
  const openEdit = (r) => {
    setEditing({ id: r.id ?? r.Id });
    setForm({
      code: r.code ?? r.Code ?? '',
      name: r.name ?? r.Name ?? '',
      description: r.description ?? r.Description ?? '',
      isActive: (r.isActive ?? r.IsActive) !== false,
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const isNew = editing === 'new';
      const res = await fetch(isNew ? '/api/user-groups' : `/api/user-groups/${editing.id}`, {
        method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(form),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kaydedilemedi');
      showToast('success', 'Kullanıcı grubu kaydedildi');
      setEditing(null);
      load();
    } catch (e) {
      showToast('error', e.message || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!confirm) return;
    try {
      const res = await fetch(`/api/user-groups/${confirm.id ?? confirm.Id}`, { method: 'DELETE', headers: authHeaders() });
      const body = await res.json().catch(() => null);
      // Gruba bağlı kullanıcı varsa sunucu silmeyi reddeder — sessizce sınıflandırma boşaltmak
      // veri kaybıdır; kullanıcıya pasifleştirme önerilir.
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Silinemedi');
      showToast('success', 'Kullanıcı grubu silindi');
      setConfirm(null);
      load();
    } catch (e) {
      showToast('error', e.message || 'Silinemedi');
      setConfirm(null);
    }
  };

  return (
    <div style={s.container}>
      <ListPageShell
        title="Kullanıcı Grupları"
        subtitle="Kullanıcıları raporlama ve toplu bilgilendirme için sınıflandırır."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Grup</button>}
        toolbar={<button style={s.btnSecondary} onClick={load}>Yenile</button>}
      >
        <div className="sec-card" style={{ marginBottom: 13 }}>
          <div className="sec-card-hint" style={{ lineHeight: 1.55 }}>
            Kullanıcı grubu <strong>yetki taşımaz</strong> — rol, menü veya veri yetkisi ile bağlantısı
            yoktur. Yetki rol veya kullanıcı üzerinden verilir. Grup yalnız raporlama kırılımı, toplu
            bilgilendirme ve giriş gün/saat kısıtı kriteri olarak kullanılır.
          </div>
        </div>

        <table className="c360-table" style={{ width: '100%' }}>
          <thead>
            <tr><th style={{ width: 140 }}>Kod</th><th>Ad</th><th>Açıklama</th><th style={{ width: 100 }}>Durum</th><th style={{ width: 150 }}>İşlem</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>Yükleniyor…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: 'center', padding: 24 }}>Henüz kullanıcı grubu tanımlanmamış</td></tr>
            )}
            {!loading && rows.map((r) => {
              const id = r.id ?? r.Id;
              const active = (r.isActive ?? r.IsActive) !== false;
              return (
                <tr key={id}>
                  <td><span className="c360-erp-chip">{r.code ?? r.Code}</span></td>
                  <td>{r.name ?? r.Name}</td>
                  <td>{r.description ?? r.Description ?? '-'}</td>
                  <td><span className={`sec-badge sec-badge--${active ? 'ok' : 'muted'}`}>{active ? 'Aktif' : 'Pasif'}</span></td>
                  <td>
                    {canEdit && <button type="button" style={s.btnSecondary} onClick={() => openEdit(r)}>Değiştir</button>}
                    {canDelete && <button type="button" style={{ ...s.btnDanger, marginLeft: 6 }} onClick={() => setConfirm(r)}>Sil</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ListPageShell>

      {editing && (
        <div style={s.overlay} onClick={() => setEditing(null)}>
          <div style={{ ...s.modal, maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>{editing === 'new' ? 'Yeni Kullanıcı Grubu' : 'Kullanıcı Grubunu Değiştir'}</h3>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span className="sec-card-label">Kod</span>
                  <span className="gp-num">
                    <input type="text" value={form.code} placeholder="örn. SAHA"
                      onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} />
                  </span>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span className="sec-card-label">Ad</span>
                  <span className="gp-num">
                    <input type="text" value={form.name} placeholder="örn. Saha Ekibi"
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                  </span>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span className="sec-card-label">Açıklama</span>
                  <textarea className="sec-note-input" value={form.description}
                    onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
                </label>
                <label className="sec-inline-check">
                  <input type="checkbox" checked={form.isActive}
                    onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />
                  Aktif
                </label>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnPrimary} disabled={saving} onClick={save}>{saving ? 'Kaydediliyor…' : 'Kaydet'}</button>
              <button style={s.btnSecondary} onClick={() => setEditing(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <div style={s.overlay} onClick={() => setConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}>
              <p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>
                <strong>"{confirm.code ?? confirm.Code}"</strong> kodlu kullanıcı grubunu silmek istediğinize emin misiniz?
                Gruba bağlı kullanıcı varsa silme reddedilir.
              </p>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={remove}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setConfirm(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default UserGroupsPage;
